/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import fieldDefinitions from "@eisinc/App/Widgets/SearchEngineOptimization/Fields";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import * as React from "react";
import { connect } from "react-redux";

// Extentions
import Brand from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaBrand";
import BreadcrumbList from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaBreadcrumbList";
import CollectionPage from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaCollectionPage";
import ContactPage from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaContactPage";
import LocalBusiness from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaLocalBusiness";
import Organization from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaOrganization";
import Product from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaProduct";
import SiteNavigationElement from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaSiteNavigationElement";
import WebPage from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaWebPage";
import Website from "@eisinc/App/Widgets/SearchEngineOptimization/SchemaWebSite";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface Jsonld {
    [key: string]: string | number | Jsonld | Jsonld[] | Jsonld[][] | undefined;
}
export type ActiveState = string | number | boolean | undefined;
export interface SeoProps {
    state: ApplicationState;
    fields: any;
    currentPage: string;
    url: string;
    payload: { [key: string]: any };
}

interface SeoExtention {
    activate: (props: SeoProps) => ActiveState;
    json: (props: SeoProps) => Jsonld;
}

const mapStateToProps = (state: ApplicationState) => ({
    state,
    currentPage: getCurrentPage(state)?.name,
    url: `https://${state?.context?.website?.uri?.split("/")[2].split(":")[0]}`,
    payload: {},
});

interface SeoGeneratorStyles {
    container?: InjectableCss;
}

const seoGeneratorStyles: SeoGeneratorStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = seoGeneratorStyles;
const SeoGenerator = (props: SeoProps & HasShellContext) => {
    if (props.shellContext.isInShell) {
        // to prevent white screen and display a placeholder when authoring the widget
        return (
            <StyledWrapper {...styles.container} data-test-selectore="SearchEngineOptimization">
                {translate("SearchEngineOptimization Widget")} v.2021.0.0
            </StyledWrapper>
        );
    }
    const use = (extention: SeoExtention) => !!extention.activate(props) && <Jsonld json={extention.json(props)} />;

    return (
        <>
            {use(Organization)}
            {use(Website)}
            {use(WebPage)}
            {use(ContactPage)}
            {use(LocalBusiness)}
            {use(BreadcrumbList)}
            {use(SiteNavigationElement)}
            {use(CollectionPage)}
            {use(Product)}
            {use(Brand)}
        </>
    );
};

const Jsonld = ({ json }: { json: Jsonld }) => {
    // eslint-disable-next-line react/no-danger
    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
};

const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    displayName: "Search Engine Optimization",
    allowedContexts: ["Header"],
    fieldDefinitions,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(SeoGenerator)),
    definition,
};

export default widgetModule;
